import { useMemo, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import type { NsEdge, NsMap, NsRecordFamily } from "@/api/netsuiteMap";
import { MONO, TONES } from "./shared";

// The system map: NetSuite in the middle, each connected system around it.
// Box positions come from systems[].pos in config/netsuite_map.yml (a
// 1160x750 canvas); the NetSuite box lists the record families and who
// creates them.
const W = 170;
const H = 64;
const CORE = { x: 440, y: 70, w: 280, h: 480 };
const ZONES: [string, number, number][] = [
  ["SELL & DELIVER", 20, 52],
  ["OUR APPS", 225, 52],
  ["OUR APPS", 765, 52],
  ["BUY & GET PAID", 970, 24],
  ["REPORTING", 20, 628],
];
const EDGE = "#9AA6B2";
const CORE_SUB = "#AEB8C2";

interface Box_ { x: number; y: number; cx: number; cy: number; r: number; b: number }

const onKey = (fn: () => void) => (e: KeyboardEvent) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    fn();
  }
};

export function SystemMap({ map, sysFilter, onSelectSystem }: {
  map: NsMap;
  sysFilter: string | null;
  onSelectSystem: (id: string | null) => void;
}) {
  const [famSel, setFamSel] = useState<number | null>(null);
  const { systems, edges, integrations, record_sources: sources } = map;

  const { counts, writers } = useMemo(() => {
    const c: Record<string, number> = {};
    const w = new Set<string>();
    integrations.forEach((i) => {
      c[i.owner] = (c[i.owner] ?? 0) + 1;
      if (i.direction === "W" || i.direction === "RW") w.add(i.owner);
    });
    return { counts: c, writers: w };
  }, [integrations]);

  const box = (id: string): Box_ => {
    const [x, y] = systems[id].pos!;
    return { x, y, cx: x + W / 2, cy: y + H / 2, r: x + W, b: y + H };
  };

  // A system with no integrations of its own (SERA, Emburse) still writes when its box says so.
  const writesToNetsuite = (id: string) => writers.has(id) || (!counts[id] && !!systems[id].writes);

  function pathFor(e: NsEdge): string {
    const { from: a, to: b } = e;
    if (e.kind === "netsuite") {
      const n = box(a);
      if (a === "bihub") {
        return `M${n.cx} ${n.y} C ${n.cx} ${CORE.y + CORE.h + 40}, ${CORE.x + 60} ${CORE.y + CORE.h + 50}, ${CORE.x + 70} ${CORE.y + CORE.h}`;
      }
      const left = n.cx < CORE.x;
      const y = Math.min(Math.max(n.cy, CORE.y + 20), CORE.y + CORE.h - 20);
      const x1 = left ? n.r : n.x;
      const x2 = left ? CORE.x : CORE.x + CORE.w;
      const mx = (x1 + x2) / 2;
      return `M${x1} ${n.cy} C ${mx} ${n.cy}, ${mx} ${y}, ${x2} ${y}`;
    }
    const A = box(a);
    const B = box(b);
    // Hand-routed lines that would otherwise cross the NetSuite box.
    if (a === "resdash" && b === "pomanager") return `M${A.cx + 40} ${A.y} C ${A.cx + 40} ${A.y - 120}, 742 ${B.cy + 200}, 742 ${B.cy} S 755 ${B.cy - 6}, ${B.x} ${B.cy - 8}`;
    if (a === "pomanager" && b === "lightreach") return `M${A.r} ${A.cy + 8} C ${A.r + 25} ${A.cy + 8}, ${B.x - 25} ${B.cy - 8}, ${B.x} ${B.cy - 8}`;
    if (a === "lightreach" && b === "fpaccounting") return `M${A.x} ${A.cy + 8} C ${A.x - 25} ${A.cy + 8}, ${B.r + 25} ${B.cy - 8}, ${B.r} ${B.cy - 8}`;
    if (a === "fspsql" && b === "mktdash") return `M${A.cx} ${A.b} C ${A.cx} ${A.b + 34}, ${B.cx} ${B.b + 34}, ${B.cx} ${B.b}`;
    if (A.cy === B.cy) return `M${A.r} ${A.cy} L ${B.x} ${B.cy}`;
    const left = A.cx < B.cx;
    const x1 = left ? A.r : A.x;
    const x2 = left ? B.x : B.r;
    const mx = (x1 + x2) / 2;
    return `M${x1} ${A.cy} C ${mx} ${A.cy}, ${mx} ${B.cy}, ${x2} ${B.cy}`;
  }

  const drawable = edges.filter((e) => systems[e.from]?.pos && (e.kind === "netsuite" || systems[e.to]?.pos));

  const famSources = (f: NsRecordFamily) => {
    const used = new Set(f.rows.flatMap((r) => r.by.map((b) => b.source)));
    return { ids: sources.filter((s) => s.id !== "manual" && used.has(s.id)), manual: used.has("manual") };
  };

  // What's lit: a selected system and the systems it feeds or is fed by, or
  // every system behind a selected record family.
  let litNodes = new Set<string>();
  let litEdge = (_e: NsEdge) => false;
  let note: ReactNode = "Showing all systems.";
  if (famSel !== null) {
    const fam = map.records[famSel];
    const src = famSources(fam);
    litNodes = new Set(src.ids.flatMap((s) => s.systems ?? [s.id]));
    litEdge = (e) => e.kind === "netsuite" && litNodes.has(e.from);
    note = (
      <>
        <b>{fam.family}</b> come from {src.ids.map((s) => s.label).join(", ")}
        {src.manual ? ", and some are still keyed by hand" : ""}. The table below shows which system does what.{" "}
        <Button size="small" onClick={() => setFamSel(null)} sx={{ p: 0, minWidth: 0 }}>Show all systems</Button>
      </>
    );
  } else if (sysFilter) {
    litNodes = new Set([sysFilter]);
    edges.forEach((e) => {
      if (e.kind !== "feeds") return;
      if (e.from === sysFilter) litNodes.add(e.to);
      if (e.to === sysFilter) litNodes.add(e.from);
    });
    litEdge = (e) => e.from === sysFilter || (e.kind === "feeds" && e.to === sysFilter);
    const n = integrations.filter((i) => i.owner === sysFilter || i.systems?.includes(sysFilter)).length;
    note = (
      <>
        Showing <b>{systems[sysFilter].label}</b>: {n} integration point{n === 1 ? "" : "s"} in the inventory below.{" "}
        <Button size="small" onClick={() => onSelectSystem(null)} sx={{ p: 0, minWidth: 0 }}>Show all systems</Button>
      </>
    );
  }
  const dim = famSel !== null || !!sysFilter;

  const selectSystem = (id: string) => {
    setFamSel(null);
    onSelectSystem(sysFilter === id ? null : id);
  };
  const selectFamily = (i: number) => {
    if (sysFilter) onSelectSystem(null);
    setFamSel(famSel === i ? null : i);
  };

  const famText = (f: NsRecordFamily) => {
    const src = famSources(f);
    const parts = src.ids.map((s) => s.short);
    let text = "";
    let n = 0;
    for (; n < parts.length; n++) {
      const t = parts.slice(0, n + 1).join(", ");
      if (t.length > 34) break;
      text = t;
    }
    if (n < parts.length) text += ` +${parts.length - n}`;
    if (src.manual) text += `${text ? " · " : ""}by hand`;
    return `from ${text}`;
  };

  const nodeClass = (id: string) =>
    ["node", systems[id].kind === "partner" ? "ext" : "", litNodes.has(id) ? "lit" : "", (sysFilter === id) ? "sel" : ""].join(" ");

  return (
    <Stack spacing={1}>
      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Box
          component="svg"
          viewBox="0 0 1160 750"
          role="img"
          aria-label="Diagram of systems connected to NetSuite"
          className={dim ? "dim" : ""}
          sx={{
            display: "block", width: "100%", minWidth: 860, height: "auto",
            "& .zone": { fontFamily: MONO, fontSize: 10.5, letterSpacing: ".08em", fill: (t) => t.palette.text.secondary },
            "& .edge": { fill: "none", stroke: EDGE, strokeWidth: 1.2, transition: "opacity .15s" },
            "& .edge.w": { stroke: (t) => t.palette.secondary.main, strokeWidth: 2 },
            "& .edge.r": { strokeWidth: 1.6, strokeDasharray: "5 4" },
            "& .node": { cursor: "pointer", outline: "none", transition: "opacity .15s" },
            "& .node rect.box": { fill: (t) => t.palette.background.paper, stroke: (t) => t.palette.text.primary, strokeWidth: 1.4 },
            "& .node.ext rect.box": { fill: (t) => t.palette.background.default, stroke: (t) => t.palette.text.secondary, strokeDasharray: "4 3" },
            "& .node:hover rect.box, & .node.sel rect.box, & .node:focus-visible rect.box": { stroke: (t) => t.palette.secondary.main, strokeWidth: 2.2 },
            "& .node text": { fill: (t) => t.palette.text.primary, fontSize: 13, fontWeight: 600 },
            "& .node text.sub": { fill: (t) => t.palette.text.secondary, fontWeight: 400, fontSize: 11 },
            "& .node text.wr": { fill: TONES.accent.fg, fontSize: 11, fontWeight: 600 },
            "& .node text.fd": { fill: (t) => t.palette.text.secondary, fontSize: 11, fontWeight: 400, fontStyle: "italic" },
            "& .badge": { fill: (t) => t.palette.secondary.main },
            "& .node text.badge-t": { fill: "#fff", fontFamily: MONO, fontSize: 11, fontWeight: 500 },
            "&.dim .node:not(.lit)": { opacity: 0.28 },
            "&.dim .edge:not(.lit)": { opacity: 0.12 },
            "& .fam": { cursor: "pointer", outline: "none" },
            "& .fam rect": { fill: "transparent" },
            "& .fam:hover rect, & .fam.sel rect": { fill: "rgba(255,255,255,.09)" },
            "& .fam:focus-visible rect": { stroke: (t) => t.palette.secondary.main, strokeWidth: 2 },
          }}
        >
          {ZONES.map(([t, x, y]) => <text key={`${t}${x}`} x={x} y={y} className="zone">{t}</text>)}

          <g>
            {drawable.map((e) => {
              const cls = e.kind === "feeds" ? "edge" : `edge ${writesToNetsuite(e.from) ? "w" : "r"}`;
              return <path key={`${e.from}-${e.to}`} d={pathFor(e)} className={`${cls}${litEdge(e) ? " lit" : ""}`} />;
            })}
          </g>

          <g>
            <rect x={CORE.x} y={CORE.y} width={CORE.w} height={CORE.h} rx={6} fill="#252f38" />
            <text x={CORE.x + 20} y={CORE.y + 40} fill="#F4F6F8" fontSize={26} fontWeight={750}>NetSuite</text>
            <text x={CORE.x + 20} y={CORE.y + 60} fill={CORE_SUB} fontFamily={MONO} fontSize={10.5}>BOOK OF RECORD</text>
            {map.records.map((f, i) => {
              const y = CORE.y + 92 + i * 44;
              return (
                <g key={f.family}>
                  <line x1={CORE.x + 20} x2={CORE.x + CORE.w - 20} y1={y - 14} y2={y - 14} stroke={CORE_SUB} strokeOpacity={0.35} />
                  <g
                    className={`fam${famSel === i ? " sel" : ""}`}
                    tabIndex={0}
                    role="button"
                    aria-label={`Trace ${f.family}`}
                    onClick={() => selectFamily(i)}
                    onKeyDown={onKey(() => selectFamily(i))}
                  >
                    <rect x={CORE.x + 10} y={y - 12} width={CORE.w - 20} height={40} rx={3} />
                    <text x={CORE.x + 20} y={y + 4} fill="#F4F6F8" fontSize={12.5}>{f.family}</text>
                    <text x={CORE.x + 20} y={y + 20} fill={CORE_SUB} fontSize={11}>{famText(f)}</text>
                  </g>
                </g>
              );
            })}
            <g
              className={`node${litNodes.has("netsuite") ? " lit" : ""}${sysFilter === "netsuite" ? " sel" : ""}`}
              tabIndex={0}
              role="button"
              onClick={() => selectSystem("netsuite")}
              onKeyDown={onKey(() => selectSystem("netsuite"))}
            >
              <title>{`SuiteScripts · ${counts.netsuite ?? 0} integration points`}</title>
              <rect x={CORE.x + 14} y={CORE.y + CORE.h - 50} width={CORE.w - 28} height={38} rx={4} fill="transparent" stroke={CORE_SUB} />
              <text x={CORE.x + 28} y={CORE.y + CORE.h - 26} style={{ fill: "#F4F6F8", fontSize: 12 }}>SuiteScripts → invoices, $0 bills, JEs</text>
              <circle cx={CORE.x + CORE.w - 36} cy={CORE.y + CORE.h - 31} r={11} className="badge" />
              <text x={CORE.x + CORE.w - 36} y={CORE.y + CORE.h - 27} textAnchor="middle" className="badge-t">{counts.netsuite ?? 0}</text>
            </g>
          </g>

          {Object.entries(systems).filter(([, s]) => s.pos).map(([id, s]) => {
            const { x, y } = box(id);
            const c = counts[id];
            return (
              <g key={id} className={nodeClass(id)} tabIndex={0} role="button" onClick={() => selectSystem(id)} onKeyDown={onKey(() => selectSystem(id))}>
                <title>{s.label + (c ? ` · ${c} integration points` : "")}</title>
                <rect className="box" x={x} y={y} width={W} height={H} rx={5} />
                <text x={x + 12} y={y + 21}>{s.label}</text>
                <text x={x + 12} y={y + 37} className="sub">{s.subtitle}</text>
                {s.writes && <text x={x + 12} y={y + 54} className="wr">→ {s.writes}</text>}
                {!s.writes && s.feeds && <text x={x + 12} y={y + 54} className="fd">{s.feeds}</text>}
                {!!c && (
                  <>
                    <circle cx={x + W - 4} cy={y + 4} r={11} className="badge" />
                    <text x={x + W - 4} y={y + 8} textAnchor="middle" className="badge-t">{c}</text>
                  </>
                )}
              </g>
            );
          })}
        </Box>
      </Paper>
      <Typography variant="body2" color="text.secondary">{note}</Typography>
    </Stack>
  );
}

export function MapLegend() {
  const line = (style: object) => <Box component="span" sx={{ display: "inline-block", width: 22, verticalAlign: "middle", mr: 0.75, ...style }} />;
  const sq = (style: object) => <Box component="span" sx={{ display: "inline-block", width: 14, height: 14, borderRadius: "3px", verticalAlign: "middle", mr: 0.75, ...style }} />;
  return (
    <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={0.5} sx={{ typography: "caption", color: "text.secondary", alignItems: "center" }}>
      <span><Box component="b" sx={{ color: TONES.accent.fg }}>→ POs</Box> What it writes</span>
      <span>{line({ borderTop: "2px solid", borderColor: "secondary.main" })}Writes to NetSuite</span>
      <span>{line({ borderTop: `2px dashed ${EDGE}` })}Reads only</span>
      <span>{line({ borderTop: `1px solid ${EDGE}` })}Feeds data</span>
      <span>{sq({ border: "1.5px solid", borderColor: "text.primary", bgcolor: "background.paper" })}Built in-house</span>
      <span>{sq({ border: "1.5px dashed", borderColor: "text.secondary" })}Vendor or partner</span>
    </Stack>
  );
}
